/**
 * Principals: the people, departments and groups a message, task or activity
 * can be addressed to. Pure data helpers behind `NxPrincipalPicker` — also
 * meant for the places that only SHOW recipients (a table cell, a dialog
 * footer), so every surface words and orders them the same way.
 *
 * The kit owns no directory: the caller supplies the principals (and their
 * absences) and gets plain arrays back.
 */
import type { NxHue } from '../lib/hue';
import { matchesMentionQuery } from '../Mentions/types';

export type NxPrincipalKind = 'person' | 'department' | 'group';

export type NxPrincipalId = string | number;

/** Just enough to identify a principal: a person 5 and a group 5 are different. */
export type NxPrincipalRef = { kind: NxPrincipalKind; id: NxPrincipalId };

/** A person's current absence, already worded and formatted by the caller. */
export type NxPrincipalAbsence = {
  /** Why the person is away, e.g. „Urlaub“, „krank“, „Messe“. */
  reason: string;
  /** Last day away, formatted for display (e.g. „17.07.“). */
  until: string;
  /** First day away, formatted — for the caller's own warning card. */
  from?: string;
  /** The stored substitute, if any. May be absent too (check its `absence`). */
  substitute?: NxPrincipal;
};

export type NxPrincipal = NxPrincipalRef & {
  /** Person name, or the bare name of the department / group (no „Abteilung“ prefix). */
  label: string;
  /** Secondary text on the right of a suggestion, e.g. a person's department. */
  sublabel?: string;
  /** Department: number of employees; group: number of members. */
  memberCount?: number;
  /** Kürzel of a person (e.g. „JEB“) — searched by `filterPrincipals`. */
  initials?: string;
  /** Persons only: set while the person is away. */
  absence?: NxPrincipalAbsence | null;
};

/**
 * The caller's directory lookup. Returns hits of every kind; the picker groups,
 * limits and filters them. `signal` aborts when a newer query supersedes this
 * one — pass it to `fetch`, or ignore it for a local list.
 */
export type NxPrincipalSearch = (
  query: string,
  signal: AbortSignal
) => readonly NxPrincipal[] | PromiseLike<readonly NxPrincipal[]>;

/** Hits shown per kind. `0` hides a kind, `Infinity` lifts the cap. */
export type NxPrincipalLimits = Partial<Record<NxPrincipalKind, number>>;

/** The words put in front of a department's / group's name. */
export type NxPrincipalKindLabels = { department: string; group: string };

export type NxPrincipalGroup = { kind: NxPrincipalKind; items: NxPrincipal[] };

/** Suggestion order: people first, then departments, then groups. */
export const NX_PRINCIPAL_KINDS: readonly NxPrincipalKind[] = ['person', 'department', 'group'];

/** 4 people, 3 departments, 3 groups — enough to pick from, short enough to scan. */
export const DEFAULT_PRINCIPAL_LIMITS: Readonly<Record<NxPrincipalKind, number>> = {
  person: 4,
  department: 3,
  group: 3,
};

export const DEFAULT_PRINCIPAL_KIND_LABELS: Readonly<NxPrincipalKindLabels> = {
  department: 'Abteilung',
  group: 'Gruppe',
};

/** One category colour per kind, the same wherever principals are drawn. */
export const NX_PRINCIPAL_HUES: Readonly<Record<NxPrincipalKind, NxHue>> = {
  person: 'sky',
  department: 'teal',
  group: 'violet',
};

/** Stable identity across kinds, e.g. for `v-for` keys. `5` and `'5'` are the same id. */
export function principalKey(principal: NxPrincipalRef): string {
  return `${principal.kind}:${String(principal.id)}`;
}

export function samePrincipal(a: NxPrincipalRef, b: NxPrincipalRef): boolean {
  return principalKey(a) === principalKey(b);
}

function joinWords(...words: (string | undefined)[]): string {
  return words
    .map((word) => word?.trim())
    .filter(Boolean)
    .join(' ');
}

/**
 * The display name: „Jenny Beck“, „Abteilung Service“, „Gruppe Freigabe
 * Angebote“. `count` appends a group's member count — „Gruppe Support (3)“ —
 * which is how a chip reads.
 */
export function principalLabel(
  principal: NxPrincipal,
  options: { count?: boolean; kindLabels?: Partial<NxPrincipalKindLabels> } = {}
): string {
  const words = { ...DEFAULT_PRINCIPAL_KIND_LABELS, ...options.kindLabels };
  if (principal.kind === 'department') return joinWords(words.department, principal.label);
  if (principal.kind === 'group') {
    const label = joinWords(words.group, principal.label);
    return options.count && typeof principal.memberCount === 'number'
      ? `${label} (${principal.memberCount})`
      : label;
  }
  return principal.label;
}

/** The short name of a table cell: a person's first name, a department's / group's bare name. */
export function principalShortLabel(principal: NxPrincipal): string {
  if (principal.kind !== 'person') return principal.label;
  return principal.label.trim().split(/\s+/)[0] ?? principal.label;
}

/**
 * A list of principals as one line.
 * - full (default): „Gruppe Freigabe Angebote, Abteilung Service, Jenny Beck“
 *   — dialog footers („geht an …“), detail views;
 * - `short`: „Jenny +2“ — the first one's short name plus how many more,
 *   for a narrow table column.
 */
export function formatPrincipals(
  list: readonly NxPrincipal[],
  options: { short?: boolean; kindLabels?: Partial<NxPrincipalKindLabels> } = {}
): string {
  if (list.length === 0) return '';
  if (options.short) {
    const more = list.length > 1 ? ` +${list.length - 1}` : '';
    return `${principalShortLabel(list[0])}${more}`;
  }
  return list.map((principal) => principalLabel(principal, options)).join(', ');
}

/** „krank bis 07.07.“ — the absence as a chip tooltip or a suggestion tag reads it. */
export function formatAbsence(absence: NxPrincipalAbsence, untilWord = 'bis'): string {
  return joinWords(absence.reason, absence.until ? untilWord : undefined, absence.until);
}

/**
 * „Jessica Heider – krank bis 07.07., Vertretung Furkan Vural“ — the whole
 * story in one line, e.g. for the tooltip of a table cell's absence mark.
 * Empty for a principal who is not away.
 */
export function formatAbsenceNote(
  principal: NxPrincipal,
  words: { until?: string; substitute?: string } = {}
): string {
  const absence = principal.absence;
  if (!absence) return '';
  const note = `${principal.label} – ${formatAbsence(absence, words.until ?? 'bis')}`;
  return absence.substitute
    ? `${note}, ${words.substitute ?? 'Vertretung'} ${absence.substitute.label}`
    : note;
}

/** The persons of a list that are away — what the absence warning is about. */
export function absentPrincipals(list: readonly NxPrincipal[]): NxPrincipal[] {
  return list.filter((principal) => principal.kind === 'person' && !!principal.absence);
}

/**
 * Whether a department or a group is among the principals. In the „An“ field
 * that is when the caller asks HOW the collective is assigned (one takes it,
 * each on their own, for information only).
 */
export function hasCollectivePrincipal(list: readonly NxPrincipalRef[]): boolean {
  return list.some((principal) => principal.kind !== 'person');
}

/** Adds a principal at the end — unless it is already there (then the same array comes back). */
export function addPrincipal(list: readonly NxPrincipal[], principal: NxPrincipal): NxPrincipal[] {
  if (list.some((entry) => samePrincipal(entry, principal))) return list as NxPrincipal[];
  return [...list, principal];
}

/** Removes a principal (every entry with its identity); the same array comes back when absent. */
export function removePrincipal(
  list: readonly NxPrincipal[],
  principal: NxPrincipalRef
): NxPrincipal[] {
  const next = list.filter((entry) => !samePrincipal(entry, principal));
  return next.length === list.length ? (list as NxPrincipal[]) : next;
}

/**
 * „An Vertretung senden“: the absent person's substitute takes their place —
 * at the same position, and only once if the substitute is already in the
 * list. Without a stored substitute the same array comes back.
 */
export function substitutePrincipal(
  list: readonly NxPrincipal[],
  principal: NxPrincipalRef & { absence?: NxPrincipalAbsence | null }
): NxPrincipal[] {
  const substitute = principal.absence?.substitute;
  const index = list.findIndex((entry) => samePrincipal(entry, principal));
  if (!substitute || index < 0) return list as NxPrincipal[];
  const next = list.filter((entry) => !samePrincipal(entry, principal));
  if (!next.some((entry) => samePrincipal(entry, substitute))) next.splice(index, 0, substitute);
  return next;
}

/** A plain id stands for a person; a `{ kind, id }` excludes exactly that principal. */
function exclusionKey(entry: NxPrincipalId | NxPrincipalRef): string {
  return typeof entry === 'object'
    ? principalKey(entry)
    : principalKey({ kind: 'person', id: entry });
}

/**
 * Search hits as suggestion groups: people, departments, groups (in that
 * order, empty ones dropped), each capped by `limits`, without the `exclude`d
 * principals (the ones already chosen, the signed-in user) and without
 * repeats. Hits keep the order the search returned them in.
 */
export function groupPrincipals(
  hits: readonly NxPrincipal[],
  options: {
    exclude?: readonly (NxPrincipalId | NxPrincipalRef)[];
    limits?: NxPrincipalLimits;
  } = {}
): NxPrincipalGroup[] {
  const excluded = new Set((options.exclude ?? []).map(exclusionKey));
  const limits = { ...DEFAULT_PRINCIPAL_LIMITS, ...options.limits };
  const groups = new Map<NxPrincipalKind, NxPrincipal[]>(
    NX_PRINCIPAL_KINDS.map((kind) => [kind, []])
  );
  const seen = new Set<string>();
  for (const hit of hits) {
    const key = principalKey(hit);
    const items = groups.get(hit.kind);
    if (!items || excluded.has(key) || seen.has(key)) continue;
    if (items.length >= limits[hit.kind]) continue;
    seen.add(key);
    items.push(hit);
  }
  return NX_PRINCIPAL_KINDS.map((kind) => ({ kind, items: groups.get(kind) ?? [] })).filter(
    (group) => group.items.length > 0
  );
}

/**
 * A ready-made `search` for a directory the caller already holds: a
 * case-insensitive substring of the name; people are found by their Kürzel
 * and word beginnings too („jb“, „jen be“ → Jenny Beck), like @-mentions.
 */
export function filterPrincipals(all: readonly NxPrincipal[], query: string): NxPrincipal[] {
  const q = query.trim().toLowerCase();
  if (!q) return all.slice();
  return all.filter((principal) =>
    principal.kind === 'person'
      ? matchesMentionQuery({ name: principal.label, initials: principal.initials }, q)
      : principal.label.toLowerCase().includes(q)
  );
}
